import type {
  AiExecutionDependency,
  AiExecutionGraph,
  AiExecutionPlan,
  AiExecutionStep,
  AiExecutionStepState,
  AiOperationEnvelope,
} from '../../../types/aiOperational'
import { checkpointStateForStep, deriveExecutionState } from './executionStateMachine'

const graphId = (envelope: AiOperationEnvelope) =>
  `exec_graph_${envelope.workspaceId}_${envelope.createdAtIso.replace(/[^0-9A-Za-z]/g, '').slice(0, 18)}`

const dependencyId = (fromStepId: string, toStepId: string, kind: AiExecutionDependency['kind']) =>
  `dep_${kind.toLowerCase()}_${fromStepId}_${toStepId}`.replace(/[^0-9A-Za-z_]/g, '_')

const stepState = (status: AiExecutionPlan['steps'][number]['status']): AiExecutionStepState => status

const rollbackContractsForStep = (envelope: AiOperationEnvelope, operationIds: string[]) =>
  envelope.operations
    .filter((operation) => operationIds.includes(operation.id))
    .map((operation) => operation.rollback?.contract)
    .filter((contract): contract is NonNullable<typeof contract> => Boolean(contract))
    .map((contract) => contract.id)

const buildSequentialDependencies = (steps: AiExecutionPlan['steps']): AiExecutionDependency[] =>
  steps.slice(1).map((step, index) => {
    const previous = steps[index]!
    return {
      id: dependencyId(previous.id, step.id, 'SEQUENTIAL'),
      kind: 'SEQUENTIAL',
      fromStepId: previous.id,
      toStepId: step.id,
      description: `${step.title} waits for ${previous.title}.`,
      required: true,
    }
  })

const buildCheckpointDependencies = (plan: AiExecutionPlan): AiExecutionDependency[] =>
  plan.checkpoints.map((checkpoint) => ({
    id: dependencyId(checkpoint.id, checkpoint.requiredBeforeStepId, 'CHECKPOINT'),
    kind: 'CHECKPOINT',
    fromStepId: checkpoint.id,
    toStepId: checkpoint.requiredBeforeStepId,
    description: checkpoint.description,
    required: true,
  }))

export const buildExecutionGraph = (
  envelope: AiOperationEnvelope,
  plan: AiExecutionPlan,
): AiExecutionGraph => {
  const dependencies = [
    ...buildSequentialDependencies(plan.steps),
    ...buildCheckpointDependencies(plan),
  ]
  const createdAtIso = envelope.createdAtIso
  const rollbackContracts = envelope.operations
    .map((operation) => operation.rollback?.contract)
    .filter((contract): contract is NonNullable<typeof contract> => Boolean(contract))

  const steps: AiExecutionStep[] = plan.steps.map((step) => {
    const checkpointIds = plan.checkpoints
      .filter((checkpoint) => checkpoint.requiredBeforeStepId === step.id)
      .map((checkpoint) => checkpoint.id)
    return {
      id: `graph_${step.id}`,
      planStepId: step.id,
      operationIds: step.operationIds,
      title: step.title,
      description: step.description,
      state: stepState(step.status),
      authority: step.authority,
      requiresUserAction: step.requiresUserAction,
      dependencyIds: dependencies
        .filter((dependency) => dependency.toStepId === step.id)
        .map((dependency) => dependency.id),
      checkpointIds,
      rollbackContractIds: rollbackContractsForStep(envelope, step.operationIds),
    }
  })

  return {
    version: 1,
    id: graphId(envelope),
    workspaceId: envelope.workspaceId,
    state: plan.state ?? deriveExecutionState(envelope),
    risk: envelope.risk,
    summary: plan.summary,
    operationIds: envelope.operations.map((operation) => operation.id),
    steps,
    dependencies,
    checkpoints: plan.checkpoints.map((checkpoint) => {
      const requiredStep = plan.steps.find((step) => step.id === checkpoint.requiredBeforeStepId)
      return {
        ...checkpoint,
        state: checkpointStateForStep(checkpoint, requiredStep?.status ?? 'PLANNED'),
      }
    }),
    rollbackContracts,
    ...(envelope.simulation ? { simulation: envelope.simulation } : {}),
    createdAtIso,
    updatedAtIso: createdAtIso,
  }
}
